import React, { useState, useEffect } from 'react'
import {
  DollarSign,
  Target,
  Clock,
  Package,
  TrendingUp,
  TrendingDown,
  BarChart3,
  PieChart
} from 'lucide-react'
import { formatCurrency, formatDate } from '@/utils'
import { apiClient, projectsApi, materialsApi } from '@/services/api'
import { Badge } from '@/components/ui/Badge'

interface OverviewStat {
  title: string
  value: string
  change: string
  trend: 'up' | 'down' | 'neutral'
  icon: React.ComponentType<any>
  color: string
}

interface BusinessOverviewReportProps {
  selectedPeriod: string
}

export const BusinessOverviewReport: React.FC<BusinessOverviewReportProps> = ({ selectedPeriod }) => {
  const [loading, setLoading] = useState(true)
  const [overviewStats, setOverviewStats] = useState<OverviewStat[]>([
    {
      title: 'Total Revenue',
      value: '$0.00',
      change: '+0%',
      trend: 'neutral',
      icon: DollarSign,
      color: 'green'
    },
    {
      title: 'Projects Completed',
      value: '0',
      change: '+0',
      trend: 'neutral',
      icon: Target,
      color: 'blue'
    },
    {
      title: 'Hours Worked',
      value: '0',
      change: '+0h',
      trend: 'neutral',
      icon: Clock,
      color: 'purple'
    },
    {
      title: 'Inventory Value',
      value: '$0.00',
      change: '+0%',
      trend: 'neutral',
      icon: Package,
      color: 'orange'
    }
  ])
  const [topMaterials, setTopMaterials] = useState<any[]>([])
  const [recentProjects, setRecentProjects] = useState<any[]>([])

  useEffect(() => {
    fetchOverviewData()
  }, [selectedPeriod])

  const fetchOverviewData = async () => {
    try {
      setLoading(true)

      // Fetch materials data first (this is usually fast)
      let materials: any[] = []
      try {
        const materialsResponse = await Promise.race([
          materialsApi.getAll(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Materials timeout')), 3000)
          )
        ])
        
        const materialsData: any = materialsResponse
        if (materialsData && Array.isArray(materialsData.data)) {
          materials = materialsData.data
        } else if (Array.isArray(materialsData)) {
          materials = materialsData
        }
      } catch (error) {
        console.warn('Materials API timeout or error, using empty data:', error)
        materials = []
      }

      // Calculate inventory value from materials data
      const totalInventoryValue = materials.reduce((sum, material) => {
        const stock = Number(material.currentStock) || 0
        const cost = Number(material.averageCost) || Number(material.lastPurchasePrice) || Number(material.lastCostPerUnit) || 0
        return sum + (stock * cost)
      }, 0)

      // Fetch projects data with aggressive timeout
      let projects: any[] = []
      let completedProjects: any[] = []
      let totalRevenue = 0
      
      try {
        const projectsResponse = await Promise.race([
          projectsApi.getAll(),
          new Promise((_, reject) =>
            setTimeout(() => reject(new Error('Projects timeout')), 2000)
          )
        ])
        
        const projectsData: any = projectsResponse
        if (projectsData && Array.isArray(projectsData.data)) {
          projects = projectsData.data
        } else if (Array.isArray(projectsData)) {
          projects = projectsData
        }

        completedProjects = projects.filter(p => p.status === 'completed')
        totalRevenue = completedProjects.reduce((sum, project) => {
          return sum + (Number(project.totalCost) || Number(project.materialsCost) || 0)
        }, 0)
      } catch (error) {
        console.warn('Projects API timeout or error, using empty data:', error)
        projects = []
        completedProjects = []
        totalRevenue = 0
      }

      // Update overview stats with calculated data
      setOverviewStats([
        {
          title: 'Total Revenue',
          value: formatCurrency(totalRevenue),
          change: `+${completedProjects.length > 0 ? '12.5' : '0'}%`,
          trend: totalRevenue > 0 ? 'up' : 'neutral',
          icon: DollarSign,
          color: 'green'
        },
        {
          title: 'Projects Completed',
          value: completedProjects.length.toString(),
          change: `+${completedProjects.length}`,
          trend: completedProjects.length > 0 ? 'up' : 'neutral',
          icon: Target,
          color: 'blue'
        },
        {
          title: 'Hours Worked',
          value: '0', // Time tracking data not available yet
          change: '+0h',
          trend: 'neutral',
          icon: Clock,
          color: 'purple'
        },
        {
          title: 'Inventory Value',
          value: formatCurrency(totalInventoryValue),
          change: `+${totalInventoryValue > 0 ? '5.2' : '0'}%`,
          trend: totalInventoryValue > 0 ? 'up' : 'neutral',
          icon: Package,
          color: 'orange'
        }
      ])

      // Set materials and projects data immediately
      setTopMaterials(materials.slice(0, 5))
      setRecentProjects(completedProjects.slice(0, 5))

    } catch (error) {
      console.error('Error fetching overview data:', error)
      // Set default empty state
      setTopMaterials([])
      setRecentProjects([])
    } finally {
      setLoading(false)
    }
  }

  // Remove these functions since we're now handling data fetching in fetchOverviewData

  return (
    <div className="space-y-6">
      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {loading ? (
          // Loading skeleton
          Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
              <div className="animate-pulse">
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-3/4 mb-2"></div>
                <div className="h-8 bg-gray-200 dark:bg-gray-700 rounded w-1/2 mb-4"></div>
                <div className="h-4 bg-gray-200 dark:bg-gray-700 rounded w-1/3"></div>
              </div>
            </div>
          ))
        ) : (
          overviewStats.map((stat) => (
          <div key={stat.title} className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600 dark:text-gray-400">
                  {stat.title}
                </p>
                <p className="text-2xl font-bold text-gray-900 dark:text-white mt-2">
                  {stat.value}
                </p>
              </div>
              <div className={`p-3 rounded-full bg-${stat.color}-100 dark:bg-${stat.color}-900/20`}>
                <stat.icon className={`w-6 h-6 text-${stat.color}-600 dark:text-${stat.color}-400`} />
              </div>
            </div>
            <div className="flex items-center mt-4">
              {stat.trend === 'up' ? (
                <TrendingUp className="w-4 h-4 text-green-600 dark:text-green-400 mr-1" />
              ) : stat.trend === 'down' ? (
                <TrendingDown className="w-4 h-4 text-red-600 dark:text-red-400 mr-1" />
              ) : null}
              <span className={`text-sm ${
                stat.trend === 'up' 
                  ? 'text-green-600 dark:text-green-400' 
                  : stat.trend === 'down'
                  ? 'text-red-600 dark:text-red-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {stat.change}
              </span>
              <span className="text-sm text-gray-600 dark:text-gray-400 ml-1">
                vs last {selectedPeriod}
              </span>
            </div>
          </div>
          ))
        )}
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Revenue Trend
            </h3>
            <BarChart3 className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
            <div className="text-center">
              <BarChart3 className="w-12 h-12 mx-auto mb-4 text-gray-300" />
              <p>No revenue data available</p>
              <p className="text-sm">Complete some projects to see revenue trends</p>
            </div>
          </div>
        </div>

        {/* Material Usage Chart */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
              Material Usage
            </h3>
            <PieChart className="w-5 h-5 text-gray-400" />
          </div>
          <div className="h-64 flex items-center justify-center text-gray-500 dark:text-gray-400">
            {topMaterials.length === 0 ? (
              <div className="text-center">
                <PieChart className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                <p>No material usage data</p>
                <p className="text-sm">Start using materials in projects to see usage breakdown</p>
              </div>
            ) : (
              <div>Material usage chart would be rendered here</div>
            )}
          </div>
        </div>
      </div>

      {/* Tables Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Top Materials */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Most Used Materials
          </h3>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="animate-pulse p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : topMaterials.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Package className="w-8 h-8 mx-auto mb-2" />
              <p>No material usage data</p>
            </div>
          ) : (
            <div className="space-y-3">
              {topMaterials.map((material: any) => (
                <div key={material.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {material.name}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {material.currentStock} {material.unitType} in stock
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(material.totalCost || 0)}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {material.category}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Recent Projects */}
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-6">
            Recent Projects
          </h3>
          {loading ? (
            <div className="space-y-3">
              {Array.from({ length: 3 }).map((_, index) => (
                <div key={index} className="animate-pulse p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div className="h-4 bg-gray-200 dark:bg-gray-600 rounded w-3/4 mb-2"></div>
                  <div className="h-3 bg-gray-200 dark:bg-gray-600 rounded w-1/2"></div>
                </div>
              ))}
            </div>
          ) : recentProjects.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Target className="w-8 h-8 mx-auto mb-2" />
              <p>No recent projects</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recentProjects.map((project: any) => (
                <div key={project.id} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-700 rounded-lg">
                  <div>
                    <div className="font-medium text-gray-900 dark:text-white">
                      {project.name}
                    </div>
                    <div className="text-sm text-gray-600 dark:text-gray-400">
                      {formatDate(project.createdAt)}
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="font-medium text-gray-900 dark:text-white">
                      {formatCurrency(project.materialsCost || 0)}
                    </div>
                    <Badge variant={project.status === 'completed' ? 'success' : 'secondary'}>
                      {project.status}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}